import { z } from 'zod';

/**
 * Patient payments — shared domain types.
 *
 * Maps the "Record Payment" sheet onto Enter's collection-payment API:
 *   record → POST /provider/collection/payment
 *   refund → POST /provider/collection/payment/{id}/refund
 *
 * KNOWN API GAPS (raised with Enter, 2026-08):
 *  - The create payload accepts only `collectedAmount, claimId, paymentDate,
 *    memo, externalTransactionId`. Enter DOES model a payment method as
 *    `CollectionPaymentSourceType` (CARD/CHECK/OFFICE_PAYMENT/TERMINAL/…) but
 *    only on the READ model — it can't be set on create. Until Enter accepts
 *    it, `method` and `type` are recorded in the human-readable `memo`.
 *  - There is NO create-adjustment endpoint, so DISCOUNTS/write-offs cannot be
 *    posted at all — the UI ships that mode disabled.
 */

/** What the money movement represents. Mirrors the design's transaction kinds. */
export const PatientPaymentKindSchema = z.enum(['payment', 'refund']);
export type PatientPaymentKind = z.infer<typeof PatientPaymentKindSchema>;

/** Payment type (design: Copay / Estimate-prepay / No-show fee). Memo-encoded for now. */
export const PatientPaymentTypeSchema = z.enum(['copay', 'estimate', 'noshow']);
export type PatientPaymentType = z.infer<typeof PatientPaymentTypeSchema>;

/**
 * How the money was taken. `card` here means a manually-recorded card payment —
 * routing to a physical reader is Enter's `/terminal/transaction` surface and is
 * deliberately OUT OF SCOPE (per product).
 */
export const PatientPaymentMethodSchema = z.enum(['card', 'cash', 'check']);
export type PatientPaymentMethod = z.infer<typeof PatientPaymentMethodSchema>;

export const PATIENT_PAYMENT_TYPE_LABEL: Record<PatientPaymentType, string> = {
  copay: 'Copay',
  estimate: 'Estimate / prepay',
  noshow: 'No-show fee',
};

export const PATIENT_PAYMENT_METHOD_LABEL: Record<PatientPaymentMethod, string> = {
  card: 'Card',
  cash: 'Cash',
  check: 'Check',
};

export const RecordPaymentInputSchema = z.object({
  /** Enter account (practice) that owns the claim — routes the credential. */
  accountId: z.string(),
  /** Claim this payment is applied to. Optional: an unapplied patient payment. */
  claimId: z.string().optional(),
  /** Positive dollar amount. Validated as a number, sent to Enter as a string. */
  amount: z.number().positive(),
  type: PatientPaymentTypeSchema,
  method: PatientPaymentMethodSchema,
  /** Free-text note appended to the generated memo. */
  note: z.string().optional(),
});
export type RecordPaymentInput = z.infer<typeof RecordPaymentInputSchema>;

/** A payment already on the ledger — the refund picker lists these. */
export const PaymentListItemSchema = z.object({
  id: z.string(),
  /** Original amount collected, in dollars. */
  amount: z.number(),
  /** How much of it has ALREADY been refunded — caps any further refund. */
  refundedAmount: z.number(),
  /** What's still refundable (`amount - refundedAmount`, never negative). */
  refundableAmount: z.number(),
  claimId: z.string().optional(),
  paymentDate: z.string().optional(),
  memo: z.string().optional(),
});
export type PaymentListItem = z.infer<typeof PaymentListItemSchema>;

export const RefundPaymentInputSchema = z.object({
  accountId: z.string(),
  /** The payment being refunded. */
  paymentId: z.string(),
  /** Positive dollar amount; must not exceed the payment's refundable balance. */
  amount: z.number().positive(),
  /** Why — mirrors the design's refund reasons. Recorded in the memo. */
  reason: z.enum(['overpay', 'duplicate', 'recoup']),
  note: z.string().optional(),
});
export type RefundPaymentInput = z.infer<typeof RefundPaymentInputSchema>;

export const REFUND_REASON_LABEL: Record<RefundPaymentInput['reason'], string> = {
  overpay: 'Overpayment',
  duplicate: 'Duplicate payment',
  recoup: 'Insurance recoupment',
};

/** Memo for a refund, same human-readable convention as payments. */
export function buildRefundMemo(input: {
  reason: RefundPaymentInput['reason'];
  note?: string | undefined;
}): string {
  const base = `Refund · ${REFUND_REASON_LABEL[input.reason]}`;
  const note = input.note?.trim();
  return note ? `${base} — ${note}` : base;
}

export const RecordedPaymentSchema = z.object({
  /** Enter's collection-payment id. */
  id: z.string(),
  amount: z.number(),
  claimId: z.string().optional(),
  /** ISO date the payment was recorded against. */
  paymentDate: z.string(),
  memo: z.string().optional(),
});
export type RecordedPayment = z.infer<typeof RecordedPaymentSchema>;

/**
 * Build the memo Enter stores, carrying the type/method it can't model yet.
 * Deliberately human-readable — a biller reading it in Enter should understand
 * it without a decoder ring: `Copay · Cash — collected at front desk`.
 */
export function buildPaymentMemo(input: {
  type: PatientPaymentType;
  method: PatientPaymentMethod;
  note?: string | undefined;
}): string {
  const base = `${PATIENT_PAYMENT_TYPE_LABEL[input.type]} · ${PATIENT_PAYMENT_METHOD_LABEL[input.method]}`;
  const note = input.note?.trim();
  return note ? `${base} — ${note}` : base;
}
